import { useState } from "react";
import { batchApi } from "@services/batchApi";
import type {
  BatchJobEntry,
  TextUnit,
} from "@models/batch";

interface SubmitOptions {
  sourceLang: string;
  targetLang: string;
  model: string;
  textUnits: TextUnit[];
  fileName: string;
}

interface UseBatchSubmitReturn {
  submitting: boolean;
  error: string | null;
  submit: (opts: SubmitOptions) => Promise<BatchJobEntry | null>;
}

/**
 * Handles the 2-step batch translation submission:
 * 1. POST /v1/startBatchTranslation → get job_id + uploadUrl
 * 2. PUT text units to the presigned uploadUrl
 */
export const useBatchSubmit = (): UseBatchSubmitReturn => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (opts: SubmitOptions): Promise<BatchJobEntry | null> => {
    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Start batch — get job_id and upload URL
      const res = await batchApi.startBatch({
        source_lang: opts.sourceLang,
        target_lang: [opts.targetLang],
        fileExtension: "json",
        batch_size: 15,
        // The backend forces batch_size_prompt=1 for every mode except
        // rmi_translation, so with the forward mode used here a value >1 is
        // silently ignored. Send 1 to reflect the effective behaviour.
        batch_size_prompt: 1,
        llm_mode: "forward",
        llm_model: opts.model,
        enable_cache: true,
      });

      const jobId = res.job_id || res.jobId;
      const uploadUrl = res.uploadUrl || res.uploadURL || res.upload_url;

      if (!jobId) {
        throw new Error("No job_id returned from API");
      }
      if (!uploadUrl) {
        throw new Error("No uploadUrl returned from API");
      }

      // Step 2: Upload text units to presigned URL
      await batchApi.uploadTextUnits(uploadUrl, opts.textUnits);

      const now = new Date().toISOString();
      const entry: BatchJobEntry = {
        jobId,
        fileName: opts.fileName,
        sourceLang: opts.sourceLang,
        targetLang: opts.targetLang,
        model: opts.model,
        totalUnits: opts.textUnits.length,
        status: "submitted",
        completedUnits: 0,
        createdAt: now,
        updatedAt: now,
      };
      return entry;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit batch job");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, submit };
};
